// src/utils/fileMatching.js
// Rattachement de fichiers (images, PDF) à des œuvres déjà créées, d'après leur numéro d'inventaire.
// Fonctions pures, sans dépendance à React, au stockage ni à la base : testables seules.
import { fold } from "./importHelpers";

export const IMAGE_RE = /\.(jpe?g|png|webp|gif)$/i;
export const PDF_RE = /\.pdf$/i;
// Formats courants dans les photothèques de galerie, que le navigateur ne sait pas afficher : signalés, pas importés.
export const UNSUPPORTED_IMAGE_RE = /\.(tiff?|heic|heif|bmp|psd|raw|cr2|nef|arw|dng)$/i;
export const MAX_FILE_BYTES = 15 * 1024 * 1024;

// Fichiers parasites des archives (macOS, Windows, fichiers cachés)
export function isNoise(path) {
  const parts = String(path || "").split("/").filter(Boolean);
  const name = parts[parts.length - 1] || "";
  return parts.some((p) => p === "__MACOSX") || name.startsWith(".") || /^(thumbs\.db|desktop\.ini)$/i.test(name);
}

export function baseName(path) {
  const name = String(path || "").split("/").filter(Boolean).pop() || "";
  return name.replace(/\.[A-Za-z0-9]{1,5}$/, "");
}

// Type de document deviné d'après le nom du fichier (mêmes règles que l'import ZIP existant).
const DOC_RULES = [
  { pattern: /certif|authenticit|auth/i, type: "certificate", label: "Certificat d'authenticité" },
  { pattern: /facture|invoice|achat|receipt/i, type: "invoice", label: "Facture" },
  { pattern: /constat|conservation|condition/i, type: "condition_report", label: "Rapport de conservation" },
  { pattern: /exposition|exhibit|catalog/i, type: "exhibition", label: "Document d'exposition" },
];
export function docTypeFromName(filename) {
  for (const r of DOC_RULES) if (r.pattern.test(filename)) return { type: r.type, label: r.label };
  return { type: "other", label: "Autre" };
}

// Rapproche chaque fichier d'une œuvre. entries : [{ path, size }], artworks : [{ id, inventory_number, ... }].
// Priorité : 1) nom du fichier égal au numéro ou commençant par lui ; 2) nom contenant le numéro (si assez long et explicite) ;
// 3) dossier portant le numéro. À priorité égale, le numéro le plus long l'emporte (REF-12-2 avant REF-12).
export function matchFilesToArtworks(entries, artworks) {
  const refs = (artworks || [])
    .filter((a) => a && a.inventory_number != null && String(a.inventory_number).trim() !== "")
    .map((a) => ({ artwork: a, key: fold(a.inventory_number) }))
    .filter((r) => r.key);

  const result = { matched: [], unmatched: [], unsupported: [], tooLarge: [], ambiguous: [] };

  for (const entry of entries || []) {
    const path = String(entry.path || "");
    if (isNoise(path)) continue;
    const isImg = IMAGE_RE.test(path);
    const isPdf = PDF_RE.test(path);
    if (!isImg && !isPdf) { if (UNSUPPORTED_IMAGE_RE.test(path)) result.unsupported.push(entry); continue; }
    if (entry.size > MAX_FILE_BYTES) { result.tooLarge.push(entry); continue; }

    const base = fold(baseName(path));
    const folders = path.split("/").filter(Boolean).slice(0, -1).map(fold);
    let best = null; // { tier, len, refs: [...] }

    const consider = (tier, r) => {
      const len = r.key.length;
      if (!best || tier < best.tier || (tier === best.tier && len > best.len)) best = { tier, len, refs: [r] };
      else if (tier === best.tier && len === best.len && !best.refs.includes(r)) best.refs.push(r);
    };

    for (const r of refs) {
      if (base === r.key || base.startsWith(r.key + " ")) consider(1, r);
      else if (r.key.length >= 4 && ` ${base} `.includes(` ${r.key} `)) consider(2, r);
      else if (folders.some((f) => f === r.key || f.startsWith(r.key + " "))) consider(3, r);
    }

    if (!best) result.unmatched.push(entry);
    else if (new Set(best.refs.map((r) => r.artwork.id)).size > 1) result.ambiguous.push(entry);
    else result.matched.push({ entry, artwork: best.refs[0].artwork, kind: isImg ? "image" : "pdf", tier: best.tier });
  }
  return result;
}

// Regroupe par œuvre, en écartant ce qui est déjà rattaché (même nom de fichier) pour qu'une reprise ne duplique rien.
export function groupByArtwork(matched) {
  const byArtwork = new Map();
  for (const m of matched) {
    const a = m.artwork;
    const name = String(m.entry.path).split("/").filter(Boolean).pop();
    const images = Array.isArray(a.images) ? a.images : [];
    let docs = [];
    try { docs = typeof a.documents === "string" ? JSON.parse(a.documents || "[]") : (a.documents || []); } catch { docs = []; }
    const stem = baseName(name);
    const already = m.kind === "image"
      ? images.some((i) => i && i.filename === name)
      : (Array.isArray(docs) ? docs : []).some((d) => d && (d.name === stem || d.filename === name));
    if (!byArtwork.has(a.id)) byArtwork.set(a.id, { artwork: a, images: [], pdfs: [], skipped: 0 });
    const g = byArtwork.get(a.id);
    if (already) g.skipped += 1;
    else (m.kind === "image" ? g.images : g.pdfs).push(m.entry);
  }
  return [...byArtwork.values()];
}
